import React, { useState, useEffect } from 'react';
import { adminAPI } from '../services/api';
import { dashboardService } from '../services/dashboardService';
import { 
  Users, 
  FileText, 
  GraduationCap, 
  DollarSign, 
  TrendingUp, 
  AlertCircle,
  CheckCircle2,
  RefreshCw,
  Download,
  BarChart3,
  Shield,
  Database,
  Clock,
  Activity,
  Filter,
  MoreVertical,
  X,
  Calendar,
  Eye,
  Settings,
  AlertTriangle
} from 'lucide-react';

// Modern Modal Component
const Modal = ({ isOpen, onClose, title, children, size = 'md' }) => {
  if (!isOpen) return null;

  const sizeClasses = {
    sm: 'max-w-md',
    md: 'max-w-2xl',
    lg: 'max-w-4xl',
    xl: 'max-w-6xl'
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50 backdrop-blur-sm">
      <div className={`bg-white rounded-2xl shadow-2xl w-full ${sizeClasses[size]} transform transition-all duration-300 scale-100`}>
        <div className="flex items-center justify-between p-6 border-b border-gray-100">
          <h2 className="text-xl font-bold text-gray-900">{title}</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors duration-200"
          >
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>
        <div className="max-h-[70vh] overflow-y-auto">
          {children}
        </div>
      </div>
    </div>
  );
};

// Report Generation Modal
const ReportModal = ({ isOpen, onClose, onGenerate }) => {
  const [reportType, setReportType] = useState('financial');
  const [dateRange, setDateRange] = useState({
    startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0]
  });
  const [format, setFormat] = useState('pdf');

  const handleGenerate = () => {
    onGenerate({
      type: reportType,
      dateRange,
      format
    });
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Generate Report" size="md">
      <div className="p-6 space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Report Type
          </label>
          <select
            value={reportType}
            onChange={(e) => setReportType(e.target.value)}
            className="w-full border border-gray-300 rounded-xl px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="financial">Financial Report</option>
            <option value="user_activity">User Activity Report</option>
            <option value="system_health">System Health Report</option>
            <option value="comprehensive">Comprehensive Report</option>
          </select>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Start Date
            </label>
            <input
              type="date"
              value={dateRange.startDate}
              onChange={(e) => setDateRange(prev => ({ ...prev, startDate: e.target.value }))}
              className="w-full border border-gray-300 rounded-xl px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              End Date
            </label>
            <input
              type="date"
              value={dateRange.endDate}
              onChange={(e) => setDateRange(prev => ({ ...prev, endDate: e.target.value }))}
              className="w-full border border-gray-300 rounded-xl px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Export Format
          </label>
          <div className="flex space-x-4">
            {['pdf', 'excel', 'csv'].map((fmt) => (
              <label key={fmt} className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="radio"
                  value={fmt}
                  checked={format === fmt}
                  onChange={(e) => setFormat(e.target.value)}
                  className="text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700">{fmt.toUpperCase()}</span>
              </label>
            ))}
          </div>
        </div>

        <div className="flex justify-end space-x-3 pt-4">
          <button
            onClick={onClose}
            className="px-6 py-3 border border-gray-300 rounded-xl text-gray-700 hover:bg-gray-50 transition-colors duration-200"
          >
            Cancel
          </button>
          <button
            onClick={handleGenerate}
            className="px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl hover:from-blue-700 hover:to-blue-800 transition-all duration-200 flex items-center space-x-2"
          >
            <Download className="h-4 w-4" />
            <span>Generate Report</span>
          </button>
        </div>
      </div>
    </Modal>
  );
};

// Enhanced Stats Card Component
const StatsCard = ({ title, value, subtitle, icon: Icon, trend, color = 'blue', loading }) => {
  const colorClasses = {
    blue: 'from-blue-500 to-blue-600',
    green: 'from-emerald-500 to-emerald-600',
    purple: 'from-purple-500 to-purple-600',
    orange: 'from-orange-500 to-orange-600',
    red: 'from-rose-500 to-rose-600'
  };

  const bgClasses = {
    blue: 'bg-blue-50 text-blue-600',
    green: 'bg-emerald-50 text-emerald-600',
    purple: 'bg-purple-50 text-purple-600',
    orange: 'bg-orange-50 text-orange-600',
    red: 'bg-rose-50 text-rose-600'
  };

  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-6 hover:shadow-lg transition-all duration-300 group">
      <div className="flex justify-between items-start mb-4">
        <div className={`p-3 rounded-xl ${bgClasses[color]} transition-colors duration-300`}>
          <Icon className="h-6 w-6" />
        </div>
        <button className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 text-gray-400 hover:text-gray-600">
          <MoreVertical className="h-4 w-4" />
        </button>
      </div>
      
      <div className="space-y-2">
        <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wide">
          {title}
        </h3>
        
        <div className="flex items-end justify-between">
          <div>
            <div className="text-2xl font-bold text-gray-900">
              {loading ? (
                <div className="flex items-center space-x-2">
                  <RefreshCw className="h-4 w-4 animate-spin" />
                  <span className="text-lg">Loading...</span>
                </div>
              ) : (
                value
              )}
            </div>
            
            {subtitle && (
              <p className="text-sm text-gray-500 mt-1">{subtitle}</p>
            )}
          </div>
          
          {trend && (
            <div className={`flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium ${
              trend.direction === 'up' 
                ? 'bg-emerald-50 text-emerald-700' 
                : 'bg-rose-50 text-rose-700'
            }`}>
              {trend.direction === 'up' ? (
                <TrendingUp className="h-3 w-3" />
              ) : (
                <TrendingUp className="h-3 w-3" />
              )}
              <span>{trend.value}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Status Indicator Component
const StatusIndicator = ({ status, label }) => {
  const statusConfig = {
    online: { color: 'bg-emerald-400', pulse: 'bg-emerald-400' },
    warning: { color: 'bg-amber-400', pulse: 'bg-amber-400' },
    error: { color: 'bg-rose-400', pulse: 'bg-rose-400' },
    offline: { color: 'bg-gray-400', pulse: 'bg-gray-400' }
  };

  const config = statusConfig[status] || statusConfig.offline;

  return (
    <div className="flex items-center space-x-2">
      <div className="relative">
        <div className={`w-3 h-3 rounded-full ${config.color} animate-pulse`}></div>
        <div className={`absolute inset-0 rounded-full ${config.pulse} animate-ping`}></div>
      </div>
      <span className="text-sm font-medium text-gray-700 capitalize">{label}</span>
    </div>
  );
};

const SystemMonitoring = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [systemData, setSystemData] = useState({
    dashboardStats: null,
    revenueAnalytics: null,
    disputes: []
  });
  const [loading, setLoading] = useState(false);
  const [timeframe, setTimeframe] = useState('7d');
  const [reportModalOpen, setReportModalOpen] = useState(false);
  const [notification, setNotification] = useState(null);

  useEffect(() => {
    loadSystemData();
  }, [timeframe]);

  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 5000);
  };

  const loadSystemData = async () => {
    setLoading(true);
    try {
      const [dashboardRes, refundsRes] = await Promise.all([
        dashboardService.getStats(),
        adminAPI.getRefundRequests('pending', 1, 100).catch(() => ({ data: { refunds: [] } }))
      ]);

      if (dashboardRes.success) {
        setSystemData({
          dashboardStats: dashboardRes.data,
          disputes: refundsRes.data?.refunds || [],
          revenueAnalytics: calculateRevenueAnalytics(dashboardRes.data, refundsRes.data?.refunds || [])
        });
      }
    } catch (error) {
      console.error('Error loading system data:', error);
      showNotification('Failed to load system data', 'error');
    } finally {
      setLoading(false);
    }
  };

  const calculateRevenueAnalytics = (stats, refunds) => {
    return {
      overview: {
        totalRevenue: stats?.totalRevenue || 0,
        totalCommissions: stats?.totalCommissions || 0,
        totalPayouts: (stats?.totalRevenue || 0) - (stats?.totalCommissions || 0),
        transactionCount: stats?.completedAssignments || 0,
        pendingRefunds: refunds.length
      }
    };
  };

  const getSystemUptime = () => ({
    percentage: '99.9%',
    duration: '15 days 6 hours',
    status: 'online'
  });

  const getDatabaseStats = () => ({
    size: '45 MB',
    tables: 28,
    connections: 5,
    status: 'online'
  });

  const getSystemHealth = () => ({
    cpu: '24%',
    memory: '62%',
    disk: '38%',
    status: 'healthy'
  });

  const handleGenerateReport = async (reportConfig) => {
    try {
      // Simulate report generation
      await new Promise(resolve => setTimeout(resolve, 2000));
      showNotification(`Report generated successfully and downloaded as ${reportConfig.format.toUpperCase()}`, 'success');
    } catch (error) {
      showNotification('Failed to generate report', 'error');
    }
  };

  const handleResolveDispute = async (disputeId, resolutionData) => {
    try {
      await adminAPI.processRefund(disputeId, 'approve', resolutionData.adminNotes);
      showNotification('Refund processed successfully', 'success');
      loadSystemData();
    } catch (error) {
      showNotification('Failed to process refund', 'error');
    }
  };

  const { dashboardStats, revenueAnalytics, disputes } = systemData;
  const uptime = getSystemUptime();
  const dbStats = getDatabaseStats();
  const systemHealth = getSystemHealth();

  const completionRate = dashboardStats?.totalAssignments > 0 ? 
    Math.round((dashboardStats.completedAssignments / dashboardStats.totalAssignments) * 100) : 0;

  return (
    <div className="min-h-screen bg-gray-50/30">
      {/* Notification */}
      {notification && (
        <div className={`fixed top-4 right-4 z-50 p-4 rounded-xl shadow-lg border-l-4 ${
          notification.type === 'success' 
            ? 'bg-emerald-50 border-emerald-400 text-emerald-700'
            : 'bg-rose-50 border-rose-400 text-rose-700'
        } transition-all duration-300 transform translate-x-0`}>
          <div className="flex items-center space-x-3">
            {notification.type === 'success' ? (
              <CheckCircle2 className="h-5 w-5" />
            ) : (
              <AlertCircle className="h-5 w-5" />
            )}
            <span className="font-medium">{notification.message}</span>
          </div>
        </div>
      )}

      {/* Report Modal */}
      <ReportModal 
        isOpen={reportModalOpen}
        onClose={() => setReportModalOpen(false)}
        onGenerate={handleGenerateReport}
      />

      {/* Header */}
      <div className="bg-white border-b border-gray-200/60">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="p-2 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl">
                <Activity className="h-6 w-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">System Monitoring</h1>
                <p className="text-sm text-gray-600 mt-1">
                  Real-time platform analytics and performance metrics
                </p>
              </div>
            </div>
            
            <div className="flex items-center space-x-3">
              <select 
                value={timeframe}
                onChange={(e) => setTimeframe(e.target.value)}
                className="border border-gray-300 rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="24h">Last 24 Hours</option>
                <option value="7d">Last 7 Days</option>
                <option value="30d">Last 30 Days</option>
                <option value="90d">Last 90 Days</option>
              </select>
              
              <button 
                onClick={() => setReportModalOpen(true)}
                className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-4 py-2.5 rounded-xl hover:from-blue-700 hover:to-blue-800 transition-all duration-200 flex items-center space-x-2"
              >
                <Download className="h-4 w-4" />
                <span>Generate Report</span>
              </button>

              <button 
                onClick={loadSystemData}
                disabled={loading}
                className="bg-white border border-gray-300 text-gray-700 px-4 py-2.5 rounded-xl hover:bg-gray-50 transition-all duration-200 flex items-center space-x-2 disabled:opacity-50"
              >
                <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                <span>Refresh</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="p-6 space-y-6">
        {/* Key Metrics Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <StatsCard
            title="Total Users"
            value={dashboardStats?.totalUsers?.toLocaleString() || '0'}
            subtitle={`${dashboardStats?.totalStudents || '0'} students • ${dashboardStats?.totalTutors || '0'} tutors`}
            icon={Users}
            trend={{ direction: 'up', value: '+12%' }}
            loading={loading}
            color="blue"
          />
          
          <StatsCard
            title="Assignments"
            value={dashboardStats?.totalAssignments?.toLocaleString() || '0'}
            subtitle={`${dashboardStats?.completedAssignments || '0'} completed`}
            icon={FileText}
            trend={{ direction: 'up', value: '+23%' }}
            loading={loading}
            color="green"
          />
          
          <StatsCard
            title="Active Tutors"
            value={dashboardStats?.totalTutors?.toLocaleString() || '0'}
            subtitle={`${dashboardStats?.pendingTutors || '0'} pending`}
            icon={GraduationCap}
            trend={{ direction: 'up', value: '+8%' }}
            loading={loading}
            color="purple"
          />
          
          <StatsCard
            title="Platform Revenue"
            value={`$${dashboardStats?.totalRevenue?.toLocaleString() || '0'}`}
            subtitle={`$${dashboardStats?.totalCommissions?.toLocaleString() || '0'} commissions`}
            icon={DollarSign}
            trend={{ direction: 'up', value: '+15%' }}
            loading={loading}
            color="orange"
          />
        </div>

        {/* Secondary Metrics */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-2xl border border-gray-200 p-4 text-center">
            <div className="text-2xl font-bold text-gray-900">{completionRate}%</div>
            <div className="text-sm text-gray-500 mt-1">Success Rate</div>
          </div>
          
          <div className="bg-white rounded-2xl border border-gray-200 p-4 text-center">
            <div className="text-2xl font-bold text-gray-900">{uptime.percentage}</div>
            <div className="text-sm text-gray-500 mt-1">System Uptime</div>
          </div>
          
          <div className="bg-white rounded-2xl border border-gray-200 p-4 text-center">
            <div className="text-2xl font-bold text-gray-900">{dbStats.size}</div>
            <div className="text-sm text-gray-500 mt-1">Database Size</div>
          </div>
          
          <div className="bg-white rounded-2xl border border-gray-200 p-4 text-center">
            <div className="text-2xl font-bold text-gray-900">{disputes.length}</div>
            <div className="text-sm text-gray-500 mt-1">Pending Issues</div>
          </div>
        </div>

        {/* Main Content */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200/60">
          {/* Enhanced Tabs */}
          <div className="border-b border-gray-200/60">
            <nav className="flex space-x-8 px-6">
              {[
                { id: 'overview', label: 'Overview', icon: Activity },
                { id: 'analytics', label: 'Analytics', icon: BarChart3 },
                { id: 'disputes', label: 'Refund Management', icon: Shield }
              ].map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors duration-200 ${
                      activeTab === tab.id
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <Icon className="h-4 w-4" />
                    <span>{tab.label}</span>
                  </button>
                );
              })}
            </nav>
          </div>

          {/* Tab Content */}
          <div className="p-6">
            {activeTab === 'overview' && (
              <OverviewTab 
                dashboardStats={dashboardStats}
                revenueAnalytics={revenueAnalytics}
                disputes={disputes}
                dbStats={dbStats}
                uptime={uptime}
                systemHealth={systemHealth}
                loading={loading}
              />
            )}

            {activeTab === 'analytics' && (
              <AnalyticsTab 
                dashboardStats={dashboardStats}
                revenueAnalytics={revenueAnalytics}
                onGenerateReport={() => setReportModalOpen(true)}
                loading={loading}
              />
            )}

            {activeTab === 'disputes' && (
              <DisputesTab 
                disputes={disputes}
                onResolveDispute={handleResolveDispute}
                loading={loading}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Enhanced Overview Tab
const OverviewTab = ({ dashboardStats, revenueAnalytics, disputes, dbStats, uptime, systemHealth, loading }) => {
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* System Health */}
        <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl p-6 border border-gray-200/60">
          <div className="flex items-center space-x-3 mb-6">
            <div className="p-2 bg-blue-100 rounded-xl">
              <Activity className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">System Health</h3>
              <p className="text-sm text-gray-600">Real-time system performance</p>
            </div>
          </div>
          
          <div className="space-y-4">
            <div className="flex justify-between items-center p-3 bg-white rounded-xl border border-gray-200">
              <span className="text-sm font-medium text-gray-600">Server Status</span>
              <StatusIndicator status={uptime.status} label="Operational" />
            </div>
            <div className="flex justify-between items-center p-3 bg-white rounded-xl border border-gray-200">
              <span className="text-sm font-medium text-gray-600">Database</span>
              <StatusIndicator status={dbStats.status} label="Connected" />
            </div>
            <div className="flex justify-between items-center p-3 bg-white rounded-xl border border-gray-200">
              <span className="text-sm font-medium text-gray-600">System Load</span>
              <StatusIndicator status={systemHealth.status} label="Healthy" />
            </div>
          </div>
        </div>

        {/* Platform Summary */}
        <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl p-6 border border-blue-200/60">
          <div className="flex items-center space-x-3 mb-6">
            <div className="p-2 bg-blue-100 rounded-xl">
              <BarChart3 className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Platform Summary</h3>
              <p className="text-sm text-gray-600">Key performance indicators</p>
            </div>
          </div>

          <div className="space-y-4">
            <div className="flex justify-between items-center p-3 bg-white rounded-xl border border-blue-200">
              <span className="text-sm font-medium text-gray-600">Total Users</span>
              <span className="text-sm font-semibold text-gray-900">
                {dashboardStats?.totalUsers?.toLocaleString() || '0'}
              </span>
            </div>
            <div className="flex justify-between items-center p-3 bg-white rounded-xl border border-blue-200">
              <span className="text-sm font-medium text-gray-600">Active Assignments</span>
              <span className="text-sm font-semibold text-gray-900">
                {dashboardStats?.totalAssignments?.toLocaleString() || '0'}
              </span>
            </div>
            <div className="flex justify-between items-center p-3 bg-white rounded-xl border border-blue-200">
              <span className="text-sm font-medium text-gray-600">Platform Revenue</span>
              <span className="text-sm font-semibold text-green-600">
                ${revenueAnalytics?.overview?.totalRevenue?.toLocaleString() || '0'}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="bg-white rounded-2xl border border-gray-200/60 p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-amber-100 rounded-xl">
              <AlertTriangle className="h-5 w-5 text-amber-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Pending Actions</h3>
              <p className="text-sm text-gray-600">Requiring immediate attention</p>
            </div>
          </div>
          <span className="bg-amber-100 text-amber-800 px-3 py-1 rounded-full text-sm font-medium">
            {disputes.length} items
          </span>
        </div>

        {disputes.length > 0 ? (
          <div className="space-y-3">
            {disputes.slice(0, 5).map((dispute) => (
              <div key={dispute.id} className="flex items-center justify-between p-4 bg-amber-50 rounded-xl border border-amber-200">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-amber-100 rounded-lg">
                    <AlertCircle className="h-4 w-4 text-amber-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">Refund Request #{dispute.id}</p>
                    <p className="text-xs text-gray-500">
                      Amount: ${dispute.amount || '0.00'} • Created: {new Date(dispute.created_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>
                <span className="bg-amber-100 text-amber-800 px-2 py-1 rounded text-xs font-medium">
                  Action Required
                </span>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8">
            <div className="bg-green-100 rounded-full p-3 w-12 h-12 mx-auto mb-3">
              <CheckCircle2 className="h-6 w-6 text-green-600 mx-auto" />
            </div>
            <p className="text-gray-500">All systems operational</p>
            <p className="text-sm text-gray-400 mt-1">No pending issues requiring attention</p>
          </div>
        )}
      </div>
    </div>
  );
};

// Enhanced Analytics Tab
const AnalyticsTab = ({ dashboardStats, revenueAnalytics, onGenerateReport, loading }) => {
  const completionRate = dashboardStats?.totalAssignments > 0 ? 
    Math.round((dashboardStats.completedAssignments / dashboardStats.totalAssignments) * 100) : 0;

  const studentRatio = dashboardStats?.totalUsers > 0 ? 
    Math.round((dashboardStats.totalStudents / dashboardStats.totalUsers) * 100) : 0;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Platform Analytics</h2>
          <p className="text-sm text-gray-600 mt-1">Detailed performance metrics and insights</p>
        </div>
        <button 
          onClick={onGenerateReport}
          className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-4 py-2.5 rounded-xl hover:from-blue-700 hover:to-blue-800 transition-all duration-200 flex items-center space-x-2"
        >
          <Download className="h-4 w-4" />
          <span>Export Report</span>
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard title="Total Users" value={dashboardStats?.totalUsers?.toLocaleString() || '0'} />
        <MetricCard title="Active Tutors" value={dashboardStats?.totalTutors?.toLocaleString() || '0'} />
        <MetricCard title="Total Assignments" value={dashboardStats?.totalAssignments?.toLocaleString() || '0'} />
        <MetricCard title="Completion Rate" value={`${completionRate}%`} />
      </div>

      {/* Performance Metrics */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-2xl border border-gray-200/60 p-6">
          <h3 className="text-lg font-semibold mb-4">User Distribution</h3>
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-sm font-medium text-gray-600">Students</span>
              <span className="text-sm font-semibold text-gray-900">
                {dashboardStats?.totalStudents?.toLocaleString() || '0'} ({studentRatio}%)
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm font-medium text-gray-600">Tutors</span>
              <span className="text-sm font-semibold text-gray-900">
                {dashboardStats?.totalTutors?.toLocaleString() || '0'} ({100 - studentRatio}%)
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-3">
              <div 
                className="h-3 rounded-full bg-gradient-to-r from-blue-500 to-blue-600 transition-all duration-1000"
                style={{ width: `${studentRatio}%` }}
              ></div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-gray-200/60 p-6">
          <h3 className="text-lg font-semibold mb-4">Assignment Status</h3>
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-sm font-medium text-gray-600">Completed</span>
              <span className="text-sm font-semibold text-gray-900">
                {dashboardStats?.completedAssignments?.toLocaleString() || '0'}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm font-medium text-gray-600">In Progress</span>
              <span className="text-sm font-semibold text-gray-900">
                {((dashboardStats?.totalAssignments || 0) - (dashboardStats?.completedAssignments || 0)).toLocaleString()}
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-3">
              <div 
                className="h-3 rounded-full bg-gradient-to-r from-green-500 to-green-600 transition-all duration-1000"
                style={{ width: `${completionRate}%` }}
              ></div>
            </div>
          </div>
        </div>
      </div>

      {/* Revenue Analytics */}
      {revenueAnalytics && (
        <div className="bg-white rounded-2xl border border-gray-200/60 p-6">
          <h3 className="text-lg font-semibold mb-4">Revenue Overview</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <MetricCard title="Total Revenue" value={`$${revenueAnalytics.overview?.totalRevenue?.toLocaleString() || '0'}`} />
            <MetricCard title="Platform Commission" value={`$${revenueAnalytics.overview?.totalCommissions?.toLocaleString() || '0'}`} />
            <MetricCard title="Pending Refunds" value={revenueAnalytics.overview?.pendingRefunds?.toLocaleString() || '0'} />
          </div>
        </div>
      )}
    </div>
  );
};

// Enhanced Disputes Tab
const DisputesTab = ({ disputes, onResolveDispute, loading }) => {
  const [resolutionData, setResolutionData] = useState({});

  const handleResolutionSubmit = (disputeId) => {
    if (resolutionData[disputeId]?.adminNotes) {
      onResolveDispute(disputeId, resolutionData[disputeId]);
      setResolutionData(prev => ({ ...prev, [disputeId]: {} }));
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <RefreshCw className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Refund Management</h2>
          <p className="text-sm text-gray-600 mt-1">Review and process refund requests</p>
        </div>
        <div className="flex items-center space-x-2 text-sm text-gray-500">
          <AlertCircle className="h-4 w-4" />
          <span>{disputes.length} pending request{disputes.length !== 1 ? 's' : ''}</span>
        </div>
      </div>
      
      {disputes.length === 0 ? (
        <div className="text-center py-12">
          <div className="bg-green-100 rounded-full p-4 w-16 h-16 mx-auto mb-4">
            <CheckCircle2 className="h-8 w-8 text-green-600 mx-auto" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">All Clear!</h3>
          <p className="text-gray-500">No pending refund requests at this time.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {disputes.map((dispute) => (
            <div key={dispute.id} className="bg-white border border-gray-200/60 rounded-2xl p-6 hover:shadow-md transition-all duration-200">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="font-semibold text-lg text-gray-900">Refund Request #{dispute.id}</h3>
                  <div className="flex items-center space-x-4 mt-2 text-sm text-gray-600">
                    <span>Amount: <strong className="text-gray-900">${dispute.amount || '0.00'}</strong></span>
                    <span>Student ID: <strong className="text-gray-900">{dispute.student_id}</strong></span>
                    <span>Created: <strong className="text-gray-900">{new Date(dispute.created_at).toLocaleDateString()}</strong></span>
                  </div>
                </div>
                <span className="bg-amber-100 text-amber-800 px-3 py-1 rounded-full text-sm font-medium">
                  {dispute.status || 'pending'}
                </span>
              </div>
              
              <div className="mb-4 p-4 bg-gray-50 rounded-xl">
                <p className="text-sm text-gray-700"><strong>Reason:</strong> {dispute.reason}</p>
              </div>

              <div className="space-y-3">
                <label className="block text-sm font-medium text-gray-700">Resolution Notes</label>
                <textarea
                  placeholder="Enter resolution notes and actions taken..."
                  value={resolutionData[dispute.id]?.adminNotes || ''}
                  onChange={(e) => setResolutionData(prev => ({
                    ...prev,
                    [dispute.id]: { ...prev[dispute.id], adminNotes: e.target.value }
                  }))}
                  className="w-full border border-gray-300 rounded-xl px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                  rows="3"
                />
                <button
                  onClick={() => handleResolutionSubmit(dispute.id)}
                  disabled={!resolutionData[dispute.id]?.adminNotes}
                  className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-6 py-3 rounded-xl hover:from-blue-700 hover:to-blue-800 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Process Refund
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Enhanced Metric Card Component
const MetricCard = ({ title, value }) => (
  <div className="bg-white border border-gray-200/60 rounded-2xl p-4 text-center hover:shadow-md transition-all duration-200">
    <h4 className="text-sm font-medium text-gray-500 mb-2">{title}</h4>
    <p className="text-lg font-bold text-gray-900">{value}</p>
  </div>
);

export default SystemMonitoring;